import { motion, Reorder, useDragControls } from 'framer-motion';
import { ChevronDown, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CourseLessonTreeNode, CourseModuleTreeNode } from '../../../../../../lib/interfaces/courses';
import LessonRow from '../lesson-row/lesson-row';

const MIN_TITLE_LENGTH = 2;
const MAX_TITLE_LENGTH = 120;

export interface ModuleRowProps {
  /** 1-based display index */
  index: number;
  module: CourseModuleTreeNode;
  /** When provided, row is rendered as a sortable framer Reorder.Item (drag handle active). */
  sortable?: boolean;
  onTitleChange: (moduleId: string, title: string) => void;
  onDelete: (module: CourseModuleTreeNode) => void;
  onAddLesson: (moduleId: string) => void;
  onLessonTitleChange: (moduleId: string, lessonId: string, title: string) => void;
  onLessonEdit: (moduleId: string, lesson: CourseLessonTreeNode) => void;
  onLessonDelete: (moduleId: string, lesson: CourseLessonTreeNode) => void;
  onLessonsReorder: (moduleId: string, nextOrder: CourseLessonTreeNode[]) => void;
}

const ModuleRow = ({
  index,
  module,
  sortable = false,
  onTitleChange,
  onDelete,
  onAddLesson,
  onLessonTitleChange,
  onLessonEdit,
  onLessonDelete,
  onLessonsReorder
}: ModuleRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(module.title);
  const [error, setError] = useState<string | null>(null);
  const lastSubmittedRef = useRef(module.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitle(module.title);
    lastSubmittedRef.current = module.title;
    setError(null);
  }, [module.title]);

  const handleTitleChange = (next: string) => {
    setTitle(next);
    if (error) setError(null);
  };

  const handleTitleBlur = () => {
    const trimmed = title.trim();
    if (trimmed === lastSubmittedRef.current) {
      setTitle(lastSubmittedRef.current);
      setError(null);
      return;
    }
    if (trimmed.length < MIN_TITLE_LENGTH) {
      setError(`Mindestens ${MIN_TITLE_LENGTH} Zeichen.`);
      // Stay focused so the user can fix it.
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    lastSubmittedRef.current = trimmed;
    setError(null);
    onTitleChange(module._id, trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setTitle(lastSubmittedRef.current);
      setError(null);
      (e.target as HTMLInputElement).blur();
    }
  };

  const dragControls = useDragControls();

  const card = (
    <div
      className="bg-white rounded-2xl border border-[#E4E7EC]"
      style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
      <div className="flex items-center gap-3 px-4 py-3">
        {sortable && (
          <button
            type="button"
            aria-label="Verschieben"
            onPointerDown={(e) => dragControls.start(e)}
            className="text-[#888] hover:text-[#12282A] cursor-grab active:cursor-grabbing touch-none">
            <GripVertical className="w-4 h-4" />
          </button>
        )}

        <span className="text-[14px] font-semibold text-[#12282A] w-6 shrink-0">{index}.</span>

        <div className="flex-1 min-w-0 flex flex-col">
          <input
            ref={inputRef}
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleKeyDown}
            maxLength={MAX_TITLE_LENGTH}
            aria-invalid={!!error}
            className={`bg-transparent text-[15px] font-semibold text-[#12282A] outline-none border-b py-1 px-1 transition-colors ${
              error ? 'border-red-400' : 'border-transparent focus:border-[#5FD5DE]'
            }`}
            placeholder="Name des Moduls"
          />
          {error && <span className="text-[11px] text-red-500 px-1">{error}</span>}
        </div>

        <span className="text-[12px] text-[#888]">{module.lessons.length} Lektionen</span>

        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          aria-label={expanded ? 'Einklappen' : 'Ausklappen'}
          className="w-8 h-8 rounded-full border border-[#E4E7EC] hover:bg-[#F0F4FC] flex items-center justify-center text-[#12282A]">
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        <button
          type="button"
          onClick={() => onDelete(module)}
          aria-label="Modul löschen"
          className="w-8 h-8 rounded-full border border-[#E4E7EC] hover:bg-red-50 hover:border-red-300 flex items-center justify-center text-red-500">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <motion.div
        initial={false}
        animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="overflow-hidden">
        <div className="px-4 pb-4 pt-3 border-t border-[#E4E7EC] flex flex-col gap-2">
          {module.lessons.length === 0 ? (
            <p className="text-[13px] text-[#888] italic py-1">Noch keine Lektionen in diesem Modul.</p>
          ) : (
            <Reorder.Group
              axis="y"
              values={module.lessons}
              onReorder={(nextOrder) => onLessonsReorder(module._id, nextOrder)}
              /* The rail carries the nesting — inset matches the lessons' left padding. */
              className="relative flex flex-col gap-0.5 pl-[18px] before:content-[''] before:absolute before:left-[5px] before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-gradient-to-b before:from-[#5FD5DE] before:to-[#C0DE60]"
              as="div">
              {module.lessons.map((lesson, i) => (
                <LessonRow
                  key={lesson._id}
                  index={`${index}.${i + 1}`}
                  lesson={lesson}
                  sortable
                  onTitleChange={(lessonId, title) => onLessonTitleChange(module._id, lessonId, title)}
                  onEdit={(target) => onLessonEdit(module._id, target)}
                  onDelete={(target) => onLessonDelete(module._id, target)}
                />
              ))}
            </Reorder.Group>
          )}

          <button
            type="button"
            onClick={() => onAddLesson(module._id)}
            className="self-start flex items-center gap-2 text-[13px] text-[#12282A] border border-[#E4E7EC] rounded-full px-3 py-1.5 hover:bg-[#F0F4FC] transition-colors">
            <Plus className="w-3.5 h-3.5" />
            Lektion hinzufügen
          </button>
        </div>
      </motion.div>
    </div>
  );

  if (!sortable) return card;

  return (
    <Reorder.Item value={module} dragListener={false} dragControls={dragControls} as="div">
      {card}
    </Reorder.Item>
  );
};

export default ModuleRow;

export const ModuleRowSkeleton = () => (
  <div
    className="bg-white rounded-2xl border border-[#E4E7EC] px-4 py-3 flex items-center gap-3 animate-pulse"
    style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
    <div className="w-4 h-4 bg-[#E4E7EC] rounded" />
    <div className="w-6 h-4 bg-[#E4E7EC] rounded-full" />
    <div className="flex-1 h-4 bg-[#E4E7EC] rounded-full" />
    <div className="w-20 h-3 bg-[#E4E7EC] rounded-full" />
    <div className="w-8 h-8 bg-[#E4E7EC] rounded-full" />
    <div className="w-8 h-8 bg-[#E4E7EC] rounded-full" />
  </div>
);
